import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, MapPin, Trash2, Printer, Pencil } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";

const EMPTY = { warehouse_id: "", zona: "A", pasillo: "01", estanteria: "", nivel: "", posicion: "", tipo: "storage", capacidad_bultos: 0, activa: true };
const TIPOS = ["storage", "receiving", "dispatch", "quarantine", "damage", "cross_dock", "pending"];

export default function Locations() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWH, setSelectedWH] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => {
    const url = selectedWH ? `/warehouse-locations?warehouse_id=${selectedWH}` : "/warehouse-locations";
    api.get(url).then(r => setRows(r.data)).catch(()=>{});
  };
  useEffect(() => { load(); }, [selectedWH]); // eslint-disable-line
  useEffect(() => { api.get("/warehouses").then(r => setWarehouses(r.data)).catch(()=>{}); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.patch(`/warehouse-locations/${editId}`, form);
      else await api.post("/warehouse-locations", form);
      toast.success(t("saved"));
      setForm(EMPTY); setShowForm(false); setEditId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/warehouse-locations/${id}`);
    load();
  };

  const printQR = async (loc) => {
    // 4x3" thermal size for warehouse location tags
    const doc = new jsPDF({ unit: "pt", format: [288, 216] });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const codigo = String(loc.codigo || "");
    const codigoIsArabic = hasArabic(codigo);
    if (codigoIsArabic) await ensureArabicFont(doc);

    // QR code — supports Unicode (unlike CODE128). Encodes the full codigo.
    let qrDataUrl = null;
    try {
      qrDataUrl = await QRCode.toDataURL(codigo || loc.id, { width: 240, margin: 1, errorCorrectionLevel: "M" });
    } catch (e) { console.warn("Location QR generation failed:", e); }

    // CODE128 barcode as ASCII fallback (uses loc.id UUID or ASCII-safe portion)
    let bcAscii = null;
    // Extract only ASCII part or use ID as machine-scannable ASCII code
    const asciiCode = (loc.id || codigo).replace(/[^\x20-\x7E]/g, "").trim() || codigo.replace(/[^\x20-\x7E]/g, "");
    if (asciiCode) {
      try {
        const c = document.createElement("canvas");
        JsBarcode(c, asciiCode.substring(0, 40), { format: "CODE128", displayValue: false, height: 40, width: 1.4, margin: 0 });
        bcAscii = c.toDataURL("image/png");
      } catch (e) { console.warn("Location barcode failed:", e); }
    }

    // Header band
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, W, 24, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(255, 255, 255);
    doc.text("MEOS CARGO · WMS LOCATION", W / 2, 16, { align: "center" });

    // QR on the left
    if (qrDataUrl) {
      try { doc.addImage(qrDataUrl, "PNG", 12, 34, 100, 100); } catch (e) { console.warn("Location QR embed failed:", e); }
    }

    // Text on the right
    doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(15, 23, 42);
    pdfText(doc, codigo, 124, 66);

    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    pdfText(doc, `Zone: ${loc.zona || "-"}`, 124, 84);
    pdfText(doc, `Aisle: ${loc.pasillo || "-"}`, 124, 96);
    if (loc.estanteria) pdfText(doc, `Rack: ${loc.estanteria}`, 124, 108);
    if (loc.nivel) pdfText(doc, `Level: ${loc.nivel}`, 124, 120);
    if (loc.posicion) pdfText(doc, `Pos: ${loc.posicion}`, 124, 132);

    // Type + capacity pill
    doc.setFillColor(241, 245, 249);
    doc.rect(12, 144, W - 24, 22, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
    doc.text(`${(loc.tipo || "").toUpperCase()}   ·   CAP: ${loc.capacidad_bultos || "—"} pkgs`, W / 2, 158, { align: "center" });

    // ASCII barcode fallback (bottom strip)
    if (bcAscii) {
      try { doc.addImage(bcAscii, "PNG", 12, 172, W - 24, 28); } catch (e) { console.warn("Location barcode embed failed:", e); }
      doc.setFont("courier", "normal"); doc.setFontSize(7); doc.setTextColor(100, 116, 139);
      doc.text(asciiCode.substring(0, 40), W / 2, H - 6, { align: "center" });
    }
    doc.save(`Ubicacion_${codigo}.pdf`);
    toast.success("PDF");
  };

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach(r => { const k = r.zona || "—"; (g[k] = g[k] || []).push(r); });
    return g;
  }, [rows]);

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";

  return (
    <div className="space-y-4" data-testid="locations-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><MapPin className="w-5 h-5 text-red-600" /> {t("locations") || "Ubicaciones WMS"}</h2>
          <p className="text-sm text-slate-500">{rows.length}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select value={selectedWH} onChange={(e)=>setSelectedWH(e.target.value)} className={inp + " w-56"} data-testid="loc-wh-filter">
            <option value="">{t("all_warehouses")}</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.nombre}</option>)}
          </select>
          <button data-testid="new-loc-btn" onClick={()=>{setForm({...EMPTY, warehouse_id: selectedWH || warehouses[0]?.id || ""}); setEditId(null); setShowForm(true);}} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> {t("new_location")}</button>
        </div>
      </div>

      {Object.entries(grouped).map(([zona, locs]) => (
        <div key={zona} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-slate-50 border-b px-4 py-2 font-semibold text-sm">{t("zone") || "Zona"} {zona} · {locs.length}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2 p-3">
            {locs.map(l => (
              <div key={l.id} className="border border-slate-200 rounded-lg p-2 flex flex-col" data-testid={`loc-${l.codigo}`}>
                <div className="font-mono font-bold text-xs truncate">{l.codigo}</div>
                <div className="text-[10px] text-slate-500 uppercase">{t(`loc_type_${l.tipo}`) || l.tipo}</div>
                <div className="flex gap-1 mt-1">
                  <button onClick={()=>printQR(l)} className="text-xs bg-slate-100 hover:bg-slate-200 rounded px-2 py-1 flex-1 flex items-center justify-center gap-1"><Printer className="w-3 h-3" /> QR</button>
                  <button data-testid={`loc-edit-${l.codigo}`} onClick={()=>{setForm({ ...EMPTY, ...l }); setEditId(l.id); setShowForm(true);}} className="text-xs bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 rounded p-1"><Pencil className="w-3 h-3" /></button>
                  <button data-testid={`loc-del-${l.codigo}`} onClick={()=>remove(l.id)} className="text-xs bg-slate-100 hover:bg-red-50 hover:text-red-600 rounded p-1"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400"><MapPin className="w-14 h-14 mx-auto opacity-30 mb-3" /><p>{t("no_locations_add_first")}</p></div>}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg p-6 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="loc-modal">
            <div className="flex justify-between items-center"><h3 className="font-display font-semibold text-lg">{editId ? t("edit") : t("new_location")}</h3><button type="button" onClick={()=>{setShowForm(false); setEditId(null);}}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("warehouse_required")}</label>
              <select required value={form.warehouse_id} onChange={(e)=>setForm({...form, warehouse_id: e.target.value})} className={inp} data-testid="lf-wh"><option value="">—</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.nombre}</option>)}</select>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              <div><label className="text-[10px] font-semibold text-slate-600 uppercase mb-1 block">{t("zone_required")}</label><input required data-testid="lf-zona" value={form.zona} onChange={(e)=>setForm({...form, zona: e.target.value.toUpperCase()})} className={inp} placeholder="A" /></div>
              <div><label className="text-[10px] font-semibold text-slate-600 uppercase mb-1 block">{t("aisle_required")}</label><input required data-testid="lf-pasillo" value={form.pasillo} onChange={(e)=>setForm({...form, pasillo: e.target.value.toUpperCase()})} className={inp} placeholder="01" /></div>
              <div><label className="text-[10px] font-semibold text-slate-600 uppercase mb-1 block">{t("rack_short")}</label><input value={form.estanteria} onChange={(e)=>setForm({...form, estanteria: e.target.value.toUpperCase()})} className={inp} placeholder="E01" /></div>
              <div><label className="text-[10px] font-semibold text-slate-600 uppercase mb-1 block">{t("level")}</label><input value={form.nivel} onChange={(e)=>setForm({...form, nivel: e.target.value.toUpperCase()})} className={inp} placeholder="N01" /></div>
              <div><label className="text-[10px] font-semibold text-slate-600 uppercase mb-1 block">{t("position_short")}</label><input value={form.posicion} onChange={(e)=>setForm({...form, posicion: e.target.value.toUpperCase()})} className={inp} placeholder="P01" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("type")}</label>
                <select value={form.tipo} onChange={(e)=>setForm({...form, tipo: e.target.value})} className={inp} data-testid="lf-tipo">{TIPOS.map(t2 => <option key={t2} value={t2}>{t(`loc_type_${t2}`) || t2}</option>)}</select>
              </div>
              <div><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("cap_units")}</label><input type="number" min="0" value={form.capacidad_bultos} onChange={(e)=>setForm({...form, capacidad_bultos: +e.target.value})} className={inp} /></div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={()=>{setShowForm(false); setEditId(null);}} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="lf-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{editId ? t("save") : t("create")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
